import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { userService } from "@/services/userService";
import { isEmpty } from "lodash";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { passwordValidation } from "@/pages/company/schema";
import { Button, Input, Modal } from "@/components";
import { useLoadingStore } from "@/store/loadingStore";
import { toastError, toastSuccess, toHalfWidth } from "@/utils";
import { DEFAULT_ERROR_MESSAGE, DEFAULT_SUCCESS_MESSAGE } from "@/types";
import errorMessages from "@/constants/errorMessages";
import { useAuth } from "@/contexts/AuthContext";

const UpdatePasswordSchema = z
  .object({
    currentPassword: passwordValidation,
    newPassword: passwordValidation,
    confirmPassword: passwordValidation,
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: errorMessages.RESET_PASSWORD_NOT_MATCH.ja,
    path: ["confirmPassword"],
  });

type UpdatePasswordFormData = z.infer<typeof UpdatePasswordSchema>;

export const UpdatePasswordForm = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { setLoading } = useLoadingStore();
  const { logout } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<UpdatePasswordFormData>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    resolver: zodResolver(UpdatePasswordSchema),
  });

  const updateUserPassword = useMutation({
    mutationFn: (data: UpdatePasswordFormData) => {
      return userService.updateUserPassword(data);
    },
    onSuccess: (response) => {
      onClose();
      toastSuccess(response?.message || DEFAULT_SUCCESS_MESSAGE);
      setLoading(false);
      logout();
    },
    onError: (error) => {
      toastError(error?.message || DEFAULT_ERROR_MESSAGE);
      setLoading(false);
    },
  });

  const onSubmit = async (data: UpdatePasswordFormData) => {
    setLoading(true);
    updateUserPassword.mutate(data);
  };

  return (
    <Modal open={open} title="パスワード変更">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <Input
            type="password"
            label="現在のパスワード"
            placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
            {...register("currentPassword")}
            error={errors.currentPassword?.message}
            onBlur={(e) => {
              const value = e.target.value;
              setValue("currentPassword", toHalfWidth(value!));
            }}
          />
          <Input
            type="password"
            label="新しいパスワード"
            placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
            {...register("newPassword")}
            error={errors.newPassword?.message}
            onBlur={(e) => {
              const value = e.target.value;
              setValue("newPassword", toHalfWidth(value!));
            }}
          />
          <Input
            type="password"
            label="新しいパスワード（確認）"
            placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
            {...register("confirmPassword")}
            error={errors.confirmPassword?.message}
            onBlur={(e) => {
              const value = e.target.value;
              setValue("confirmPassword", toHalfWidth(value!));
            }}
          />
        </div>
        <div className="flex justify-end gap-4 mt-4">
          <Button
            buttonType="text"
            className="!h-12"
            type="button"
            onClick={() => {
              onClose();
              reset();
            }}
          >
            キャンセル
          </Button>
          <Button
            buttonType="primary"
            className="!h-12"
            type="submit"
            disabled={!isEmpty(errors)}
          >
            作成
          </Button>
        </div>
      </form>
    </Modal>
  );
};
